import { logout } from '@/features/auth';
import type { State } from '@/reducers/types';
import { UserWithMetadata } from '@/types';
import { createSlice } from '@reduxjs/toolkit';
import { checkPendingUsers, getUsers, updateUser } from './actions';

interface UsersState {
  users: UserWithMetadata[];
  pagination: {
    hasNextPage: boolean;
    currentPage: number;
  };
  limit: number;
  searchQuery: string;
  sortBy: 'email' | 'timeJoined' | 'lastLoggedIn';
  sortOrder: 'asc' | 'desc';
  hasPending: boolean;
}

const initialState: UsersState = {
  users: [],
  pagination: {
    hasNextPage: false,
    currentPage: 1,
  },
  limit: 10,
  sortBy: 'timeJoined',
  sortOrder: 'desc',
  searchQuery: '',
  hasPending: false,
};

export const { name, reducer, actions } = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setLimit: (state, action) => {
      state.limit = action.payload;
      state.users = [];
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSortBy: (state, action: { payload: 'email' | 'timeJoined' | 'lastLoggedIn' }) => {
      state.sortBy = action.payload;
      state.users = [];
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSortOrder: (state, action: { payload: 'asc' | 'desc' }) => {
      state.sortOrder = action.payload;
      state.users = [];
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSearchQuery: (state, action: { payload: string }) => {
      state.searchQuery = action.payload;
      state.users = [];
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.fulfilled, (state, action) => {
        state.users = action.payload.data;
        state.pagination.hasNextPage = !action.payload.isLast;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const { userId, status, organization } = action.payload;
        if (state.users) {
          const userIndex = state.users.findIndex((user) => user.id === userId);
          if (userIndex !== -1) {
            if (status !== undefined) {
              state.users[userIndex].accountStatus = status;
            }
            if (organization !== undefined) {
              state.users[userIndex].organization = organization;
            }
          }
        }
      })
      .addCase(checkPendingUsers.fulfilled, (state, action) => {
        state.hasPending = action.payload.hasPending;
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const { setLimit, setSortBy, setSortOrder, setCurrentPage, setSearchQuery } = actions;

export const selectUsers = (state: State) => state[name].users;
export const selectPagination = (state: State) => state[name].pagination;
export const selectLimit = (state: State) => state[name].limit;
export const selectSearchQuery = (state: State) => state[name].searchQuery;
export const selectSortBy = (state: State) => state[name].sortBy;
export const selectSortOrder = (state: State) => state[name].sortOrder;
export const selectHasPending = (state: State) => state[name].hasPending;
